import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  apiGetUtilsCountriesOptions,
  type CountrySchema,
  type RegionSchema,
} from "../api";
import { compareString } from "../utils/utils";

export type CountriesData = Record<string, CountrySchema>;

/**
 * Countries and their regions — global reference data, fetched once per session.
 * The raw map is only ever consumed through region lookups, so (unlike the
 * array-shaped catalogs) `useCountries` returns a `Countries` instance exposing
 * those lookups rather than the bare data.
 */
export class Countries {
  constructor(readonly schemas: CountriesData) {}

  get countriesEnum(): string[] {
    return Object.keys(this.schemas).sort(compareString);
  }

  getRegionSchemas(country: string): RegionSchema[] | undefined {
    return this.schemas[country]?.regions;
  }

  getRegionSchema(country: string, region: number): RegionSchema | undefined {
    return this.getRegionSchemas(country)?.find(
      (r) => r.region_enum_value === region,
    );
  }

  getRegionsMap(country: string): Map<number, string> {
    const res = new Map<number, string>();
    for (const region of this.getRegionSchemas(country) ?? []) {
      res.set(region.region_enum_value, region.region_name);
    }
    return res;
  }

  getRegionsEnum(country: string): Record<number, string> {
    return Object.fromEntries(this.getRegionsMap(country).entries());
  }
}

function countriesQuery() {
  return { ...apiGetUtilsCountriesOptions(), staleTime: Infinity };
}

/**
 * Suspends the nearest boundary until loaded, so callers always get a ready
 * instance. Memoized on the query data, which TanStack Query keeps
 * referentially stable, so the instance identity is stable across renders.
 */
export function useCountries(): Countries {
  const { data } = useSuspenseQuery(countriesQuery());
  return useMemo(() => new Countries(data.countries), [data.countries]);
}
